/**
 * Pure helpers for the Monitoring page (api-edna spec 013).
 *
 * Everything here is display logic over values the API computed. Completion,
 * silver-ready and projected times are never derived client-side.
 */

import type { BackfillJob } from '../../api/monitoring';

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse an api-edna timestamp as UTC.
 *
 * The API serializes naive UTC (`2026-10-15T12:00:00`), and a date-time string
 * without an offset parses as *local* time, which would shift every displayed
 * time by the viewer's UTC offset.
 */
export function parseApiUtc(s: string): Date {
  return new Date(HAS_OFFSET.test(s) ? s : `${s}Z`);
}

/** Local display string for an API timestamp; `—` when absent. */
export function formatApiTime(s: string | null | undefined): string {
  return s ? parseApiUtc(s).toLocaleString() : '—';
}

export type CompletionLine =
  | { kind: 'none' }
  | { kind: 'complete_unknown' }
  | { kind: 'in_silver'; completedAt: string; silverAt: string | null }
  | { kind: 'silver_pending'; completedAt: string; silverAt: string | null }
  | { kind: 'estimate'; projectedAt: string; projectedSilverAt: string | null }
  | { kind: 'no_estimate' };

const TERMINAL_STATUSES = new Set(['failed', 'deleted']);

/**
 * Which completion line a job gets. Order matters:
 * - an absent `backfill_complete` means a pre-013 API, which has no completion
 *   data at all — "estimate unavailable" would be wrong for finished jobs;
 * - complete-but-unknown comes before the silver checks, because `silver_ready`
 *   is false whenever the completion time is unknown.
 */
export function backfillCompletion(job: BackfillJob): CompletionLine {
  if (job.backfill_complete === undefined) return { kind: 'none' };
  if (job.status && TERMINAL_STATUSES.has(job.status.toLowerCase())) return { kind: 'none' };
  if (job.backfill_complete) {
    if (!job.backfill_completed_at) return { kind: 'complete_unknown' };
    const silverAt = job.silver_ready_at ?? null;
    return job.silver_ready
      ? { kind: 'in_silver', completedAt: job.backfill_completed_at, silverAt }
      : { kind: 'silver_pending', completedAt: job.backfill_completed_at, silverAt };
  }
  if (job.projected_completed_at) {
    return {
      kind: 'estimate',
      projectedAt: job.projected_completed_at,
      projectedSilverAt: job.projected_silver_ready_at ?? null,
    };
  }
  return { kind: 'no_estimate' };
}

/**
 * Bar scale for "records remaining over time": the full-history max from the API,
 * falling back to the received series (pre-013 API, or the value is unknown).
 */
export function barScale(job: BackfillJob): number {
  if (job.series_max_remaining != null && job.series_max_remaining > 0) {
    return job.series_max_remaining;
  }
  return job.series.reduce((m, p) => Math.max(m, p.records_remaining ?? 0), 0) || 1;
}

export const WINDOW_OPTIONS = [
  { hours: 1, label: 'Last hour', phrase: 'last hour' },
  { hours: 24, label: 'Last 24 hours', phrase: 'last 24 hours' },
  { hours: 72, label: 'Last 3 days', phrase: 'last 3 days' },
  { hours: 168, label: 'Last 7 days', phrase: 'last 7 days' },
  { hours: 720, label: 'Last 30 days', phrase: 'last 30 days' },
] as const;

/** Live commit history covers 1 h on every table, so this never reads the archive. */
export const DEFAULT_HOURS = 1;

/** "last 7 days" etc., for sentences about the selected window. */
export function windowPhrase(hours: number): string {
  return WINDOW_OPTIONS.find((o) => o.hours === hours)?.phrase ?? `last ${hours} hours`;
}
